import { useState, useRef, useEffect } from 'react';
import { Button } from '@cfa/react-core';
import styled from 'styled-components';
import { themeColors, themeSizes } from '../../../styles/globalStyleVars';
import { callEvent } from '../../../utils/Amplitude/AmplitudeUtilities';
import type { AlertInterface } from '../../../interfaces/AlertInterface';
import RelativeTime from '../../RelativeTime/RelativeTime';
import { AffectsMeButton } from './AffectsMeButton';
import { TypographyLineLimit } from '../../Styled/TypographyElements';
import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { TypographyStyled } from '../../Styled/Typography';
import useBreakpoints from '../../../hooks/useBreakpoints';
import useMediaQuery from '../../../hooks/useMediaQuery';

type AlertsBannerItemProps = {
  alert: AlertInterface;
  setAlertsDrawerOpen: () => void;
  setAlertChildCaseOpen: (val: AlertInterface) => void;
};

type SeeMoreProps = {
  id: string;
  title?: string | null;
};

const WarningIcon = styled(IconAlertTriangleFilled)`
  align-self: flex-start;
  margin-right: ${themeSizes.sm};
  color: ${themeColors.errorColor};
  flex-shrink: 0;
  width: ${themeSizes.lg};
  height: auto;
`;

const AlertContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex-grow: 1;
`;

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: ${themeSizes.sm};
`;

const SummaryText = styled(TypographyStyled)`
  color: ${themeColors.colorBlack};
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const SeeMoreSummaryButton = styled(Button)`
  padding: 0;
  width: auto !important;
  min-width: auto !important;
  flex-shrink: 0;
  margin-left: 2px;
  height: ${themeSizes.lg} !important;
  min-height: 0 !important;
`;

const AlertTimeWrapper = styled(TypographyStyled)`
  margin-left: ${themeSizes.sm};
  display: flex;
  flex-direction: column;
  text-align: right;
  flex-shrink: 0;
  justify-content: space-between;
`;

export const AlertsBannerItem = ({
  alert,
  setAlertsDrawerOpen,
  setAlertChildCaseOpen,
}: AlertsBannerItemProps) => {
  const breakpoints = useBreakpoints();
  const isExtraSmallScreen = useMediaQuery(breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(breakpoints.down('md'));
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const [summaryOverflow, setSummaryOverflow] = useState<boolean>(false);

  const checkOverflow = () => {
    if (isExtraSmallScreen) {
      setSummaryOverflow(false);
      return;
    }
    const el = summaryRef.current;
    if (!el) return;
    setSummaryOverflow(el.scrollWidth > el.clientWidth);
  };

  //biome-ignore lint/correctness/useExhaustiveDependencies: maintain min dependencies
  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [isExtraSmallScreen]);

  const handleSeeMoreClick = (id: string, title?: string | null) => {
    setAlertsDrawerOpen();
    callEvent('see more alert', {
      'alert id': id,
      title: title,
    });
  };

  const SeeMoreButton = ({ id, title }: SeeMoreProps) => (
    <SeeMoreSummaryButton
      variant='text'
      color='secondary'
      size={isExtraSmallScreen ? 'lg' : 'sm'}
      onPress={() => handleSeeMoreClick(id, title)}
    >
      See More
    </SeeMoreSummaryButton>
  );

  const openChildCase = (alert: AlertInterface) => {
    setAlertsDrawerOpen();
    setAlertChildCaseOpen(alert);
  };

  const handleAffectsMeClick = (alert: AlertInterface) => {
    openChildCase(alert);
    callEvent('select this affects my restaurant', {
      'alert id': alert.id,
      id: alert.majorCaseId as string,
      location: 'banner',
    });
  };

  return (
    <div className='alerts-banner-item'>
      <div
        style={{
          display: 'flex',
          margin: `${themeSizes.sm} 0 ${themeSizes.xs} 0`,
        }}
      >
        <WarningIcon role='img' aria-label='Warning triangle icon' />
        <AlertContentWrapper>
          <div
            data-testid='banner-alert-heading'
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <TypographyLineLimit
              fontWeight='bold'
              $fontColor={themeColors.colorBlack}
              $lineLimit={isExtraSmallScreen ? 3 : 2}
              style={{
                maxHeight: isExtraSmallScreen ? '72px' : themeSizes.xxl,
              }}
            >
              {alert.title}
            </TypographyLineLimit>
          </div>
          {!isExtraSmallScreen && (
            <SummaryWrapper>
              <SummaryText
                ref={summaryRef}
                variant='body2'
                text={alert.summary ?? ''}
                style={{ color: themeColors.colorBlack, padding: 0 }}
                data-testid='banner-alert-summary'
              />
              {summaryOverflow && (
                <SeeMoreButton id={alert.id} title={alert.title} />
              )}
            </SummaryWrapper>
          )}
        </AlertContentWrapper>
        <AlertTimeWrapper data-testid='banner-alert-time'>
          {!isSmallScreen && 'Updated: '}
          <RelativeTime
            date={alert.updatedOn}
            constrainSize={isSmallScreen ? 'medium' : undefined}
          />
          {isExtraSmallScreen && (
            <SeeMoreButton id={alert.id} title={alert.title} />
          )}
        </AlertTimeWrapper>
      </div>
      {alert.majorCaseId && (
        <div
          style={{
            marginTop: themeSizes.sm,
            marginLeft: themeSizes.xl,
            flexShrink: 0,
          }}
        >
          <AffectsMeButton onClick={() => handleAffectsMeClick(alert)} />
        </div>
      )}
    </div>
  );
};
